import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { Button } from 'antd';
import PageShell from '../components/page/PageShell';
import PageHeader from '../components/page/PageHeader';
import DataCard from '../components/page/DataCard';
import InviteModal from '../components/InviteModal';
import EditUserNameModal from '../components/EditUserNameModal';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";
import { displayEmail } from '../utils/displayEmail';

/**
 * Resolve MSAL account without putting unstable object references in effect deps
 * (getActiveAccount() returns a new object identity often → infinite re-fetch loop).
 */
function getMsalAccount(instance) {
    return instance.getActiveAccount() || instance.getAllAccounts()[0] || null;
}

/** Roster kinds on this screen: each maps to a backend list route, a response key and the role invited. */
const KINDS = {
    lead_advisor: {
        path: 'lead-advisors', key: 'leadAdvisors', role: 'lead_advisor', tab: 'Lead Advisors',
        title: 'Lead', accent: 'advisors', noun: 'Lead Advisor', nouns: 'Lead Advisors', invite: '+ Invite Lead Advisor',
        blurb: 'Invite Lead Advisors to your tenant and review who has been provisioned. This is separate from project engagements on the Control Plane.',
    },
    platform_admin: {
        path: 'admins', key: 'admins', role: 'platform_admin', tab: 'Administrators',
        title: 'Platform', accent: 'administrators', noun: 'Administrator', nouns: 'Administrators', invite: '+ Invite Administrator',
        blurb: 'Administrators can manage Lead Advisors, masters, the Policy Hub and platform keys. Invite only people who should have full platform access.',
    },
};

/**
 * Platform admin: invite Lead Advisors (default) or other Administrators (`kindKey="platform_admin"`) and view the roster (same tenant).
 */
const LeadAdvisorsPage = ({ kindKey = 'lead_advisor' }) => {
    const { instance, accounts } = useMsal();

    /** Stable string id — only changes when the signed-in user changes (not every render). */
    const resolvedAccount = instance.getActiveAccount() || accounts[0] || null;
    const accountKey = resolvedAccount?.homeAccountId || resolvedAccount?.localAccountId || '';

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [notice, setNotice] = useState(null);
    const kind = KINDS[kindKey];

    const fetchRoster = useCallback(async () => {
        const account = getMsalAccount(instance);
        if (!account) {
            setLoading(false);
            setLoadError('No signed-in account. Refresh the page or sign in again.');
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...apiTokenRequest,
                account,
            });
            const res = await fetch(`${apiConfig.baseUrl}/api/v1/${kind.path}`, {
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
            const text = await res.text();
            let json;
            try {
                json = text ? JSON.parse(text) : {};
            } catch {
                setRows([]);
                setLoadError(
                    'Could not read API response (often a wrong API URL or HTML instead of JSON). Check REACT_APP_API_BASE_URL.'
                );
                return;
            }
            if (res.ok && json.success) {
                setRows(json.data?.[kind.key] || []);
            } else {
                setRows([]);
                setLoadError(
                    json?.error?.message ||
                        (res.status === 403 ? 'Forbidden - platform admin only.' : `Request failed (${res.status})`)
                );
            }
        } catch (e) {
            console.error(e);
            setRows([]);
            setLoadError(e?.message || 'Network error - is the API running and CORS configured?');
        } finally {
            setLoading(false);
        }
    }, [instance, kind]);

    useEffect(() => {
        if (!accountKey) {
            setLoading(false);
            setLoadError('No signed-in account. Refresh the page or sign in again.');
            return;
        }
        fetchRoster();
    }, [accountKey, fetchRoster]);

    return (
        <PageShell>
            <PageHeader
                title={`${kind.title} ${kind.accent}`}
                subtitle={kind.blurb}
                actions={<Button type="primary" onClick={() => setInviteOpen(true)}>{kind.invite}</Button>}
            />

            {loadError && (
                <div
                    className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-5 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium"
                    role="alert"
                >
                    {loadError}
                </div>
            )}

            <DataCard className="overflow-hidden" styles={{ body: { padding: 0 } }}>
                <div className="px-10 py-8 border-b border-[color:var(--color-border)] flex items-center justify-between bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_30%,transparent)]">
                    <h2 className="text-2xl font-black text-[color:var(--color-text-hero)] tracking-tight font-outfit">Directory</h2>
                    <div className="px-4 py-2 bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] rounded-2xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-info-line)]">
                        {loading ? 'Loading…' : `${rows.length} ${rows.length === 1 ? kind.noun : kind.nouns}`}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="text-[10px] font-extrabold text-[color:var(--color-text-muted)] uppercase tracking-widest border-b border-[color:var(--color-border)] bg-[color:color-mix(in_srgb,var(--color-surface-sunken)_10%,transparent)]">
                                <th className="px-10 py-6">Name</th>
                                <th className="px-10 py-6">Email</th>
                                <th className="px-10 py-6">Added</th>
                                <th className="px-10 py-6 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[color:var(--color-border)]">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="px-10 py-20 text-center text-[color:var(--color-text-muted)] font-bold text-sm">
                                        Loading directory…
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-10 py-20 text-center text-[color:var(--color-text-muted)] font-black uppercase tracking-widest text-[10px]">
                                        {`No ${kind.nouns} yet. Use Invite to add the first one.`}
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.id} className="hover:bg-[color:color-mix(in_srgb,var(--color-bg-alt)_50%,transparent)] transition-colors">
                                        <td className="px-10 py-6 font-bold text-[color:var(--color-text-hero)]">{row.name}</td>
                                        <td className="px-10 py-6 text-[color:var(--color-text-muted)] font-medium">{displayEmail(row.email)}</td>
                                        <td className="px-10 py-6 text-[color:var(--color-text-muted)] text-sm">
                                            {row.createdAt
                                                ? formatDdMmYyyyTime(row.createdAt)
                                                : '-'}
                                        </td>
                                        <td className="px-10 py-6 text-right">
                                            <button
                                                type="button"
                                                onClick={() => setEditing(row)}
                                                className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-[color:var(--color-border)] text-[color:var(--color-text-muted)] hover:border-[color:var(--color-border-strong)] hover:text-[color:var(--color-text-hero)] transition-all"
                                                aria-label={`Edit ${row.name}`}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </DataCard>

            {notice && (
                <div
                    className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-5 py-4 text-sm text-[color:var(--color-warn-fg)] font-medium"
                    role="status"
                >
                    {notice}
                </div>
            )}

            {editing && (
                <EditUserNameModal
                    user={editing}
                    onClose={() => setEditing(null)}
                    onRemoved={(removed) => {
                        setEditing(null);
                        setNotice(`${removed.name} was removed from the application.`);
                        fetchRoster();
                    }}
                    onSaved={(warnings) => {
                        setEditing(null);
                        setNotice(warnings.length ? warnings.join(' ') : null);
                        fetchRoster();
                    }}
                />
            )}

            {inviteOpen && (
                <InviteModal
                    onClose={() => setInviteOpen(false)}
                    onInviteSuccess={fetchRoster}
                    roles={[{ value: kind.role, label: kind.noun }]}
                />
            )}
        </PageShell>
    );
};

export default LeadAdvisorsPage;
